import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppNavigate, useAppParams, useAppSelector, usePermissions } from "@app/hooks";
import { useContextAwareGetActivityById } from "@hooks/index";
import { selectCurrentProject, setActivity, setProject } from "@features/project/projectSlice";
import { getFEModuleListFromActivity } from "@utils/moduleList";
import { useEffect, useMemo } from "react";
import { setModuleList } from "@features/builder/builderSlice";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { encodeActivityId } from "@utils/projectIdEncoder";

export const useHandleActivityState = () => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { isPublic } = usePermissions();
  const { pathname } = useLocation();
  const { activity, project } = useAppSelector(selectCurrentProject);
  const { activityId } = useAppParams();
  const id = useMemo(() => Number(activityId), [activityId]);
  const isActivityIdMismatch = id && activity?.id ? id !== activity?.id : false;

  const { data: activityData, isLoading, isFetching, isError, error } = useContextAwareGetActivityById(Number(activityId) ?? 0, { skip: activityId === activity?.id });

  useEffect(() => {
    if (id && activityData && activityData.id === id) {
      const feModuleList = getFEModuleListFromActivity(activityData);
      dispatch(setActivity(activityData));
      dispatch(setModuleList(feModuleList));

      const splitPath = pathname.split("/").filter(path => path !== "")
      if (splitPath.length <= (isPublic ? 3 : 2)) {
        const activityIdToUse = isPublic 
          ? (() => {
            try {
              return encodeActivityId(id);
            } catch (error) {
              console.error('Failed to encode activity ID:', error);
              return id.toString();
            }
          })()
          : id;

        const basePath = `/builder/${activityIdToUse}`;
        const targetPath = feModuleList.length > 0
          ? `${basePath}/${feModuleList[0].path}`
          : `${basePath}/complete`;

        navigate({ path: targetPath });
      }
    }
  }, [activity, activityData, id, dispatch, pathname]);


  useEffect(() => {
    if (activityData?.project && project?.id !== activityData.project.id) {
      dispatch(setProject(activityData.project));
    }
  }, [project, dispatch, activityData]);

  return { activity, isLoading: isLoading || isActivityIdMismatch, isFetching, isError, error: error as FetchBaseQueryError };
}